/**
 * The owner-activation badge's state, normalised (ADR: 1toall-portal-api docs/ACTIVATION-BADGE-ADR.md
 * §Display). Zoom has NO field that says an owner activated; the one usable signal is the owner account's
 * last_login_time. The API sends three states plus the watching `tier`; the badge SHOWS three texts
 * (owner decision 14/9 night), identical in Thai and English:
 *
 *   API state        tier                       display     text                    tone
 *   activated        (any)                      activated   "Activate: Activated"   ok (green)
 *   needs_checking   hot | warm  (≤90 d)        pending     "Activate: Pending"     info (blue)
 *   needs_checking   roster | null | unknown    unknown     "Activate: Unknown"     neutral (grey)
 *   no_data          (any)                      unknown     "Activate: Unknown"     neutral (grey)
 *
 * WHY THE TIER MATTERS. A hot/warm account is actively polled (30 s / 5 min) and a first sign-in writes
 * last_login_time within seconds, so "no sign-in seen yet" honestly means "still waiting" → Pending. A
 * roster account (>90 d) is not watched, and activated accounts exist with no last_login_time at all, so
 * Pending there would be wrong → Unknown. "Pending" is a waiting state, never a verdict about the customer.
 *
 * Never red or amber, never a negative claim, anywhere (label, title, caption). A badge whose pipeline has
 * gone quiet (`stale` from the API, or a failing refetch) keeps its label but is demoted to neutral with a
 * "not updating" caption — the colour must not vouch for a signal nobody is refreshing.
 *
 * FAIL-SAFE. An absent payload (API rolled back → 404), a missing `state`, or a state this build does not
 * know all normalise to `no_data`; a missing or unknown `tier` normalises to null. Both land on "unknown",
 * which accuses nobody of anything — nothing unrecognised can ever render Pending.
 */

import type { ActivationState, ActivationTier, CustomerActivation } from "@/api/types";

/** The only three things the badge can show. */
export type ActivationDisplay = "activated" | "pending" | "unknown";

/** Normalise a raw `state` to one of the API's three; anything else is `no_data`. */
export function activationStateOf(raw: unknown): ActivationState {
  if (raw === "activated" || raw === "needs_checking" || raw === "no_data") return raw;
  return "no_data";
}

/** Normalise a raw `tier` to one of the API's three; anything else (absent, null, unknown) is null. */
export function activationTierOf(raw: unknown): ActivationTier | null {
  if (raw === "hot" || raw === "warm" || raw === "roster") return raw;
  return null;
}

/**
 * Is a not-yet-seen sign-in on this tier actively watched (→ Pending is honest)? EXHAUSTIVE over
 * ActivationTier: add a fourth tier to api/types.ts without deciding it here and the `never` assignment
 * stops compiling. The runtime fallback is the quiet answer (false → Unknown).
 */
function tierIsWatched(tier: ActivationTier | null): boolean {
  if (tier === null) return false;
  switch (tier) {
    case "hot":
      return true;
    case "warm":
      return true;
    case "roster":
      return false;
  }
  const _exhaustive: never = tier;
  void _exhaustive;
  return false;
}

/**
 * API state × tier → display variant. EXHAUSTIVE over ActivationState (and, via tierIsWatched, over
 * ActivationTier): a new state or tier value cannot silently render Pending — it stops compiling here.
 */
export function activationDisplayOf(
  state: ActivationState,
  tier: ActivationTier | null,
): ActivationDisplay {
  switch (state) {
    case "activated":
      return "activated";
    case "needs_checking":
      return tierIsWatched(tier) ? "pending" : "unknown";
    case "no_data":
      return "unknown";
  }
  // Unreachable today. COMPILE-TIME guard against a fourth state; the runtime return is the quiet one.
  const _exhaustive: never = state;
  void _exhaustive;
  return "unknown";
}

export interface ActivationView {
  /** The normalised API state (kept for callers/tests; the badge renders `display`). */
  state: ActivationState;
  /** The normalised tier (null when absent or unrecognised). */
  tier: ActivationTier | null;
  display: ActivationDisplay;
  /** The pipeline behind this badge is not updating → render neutral + the "not updating" caption. */
  notUpdating: boolean;
  checkedAt: string | null;
}

/**
 * Payload (possibly absent) → what the badge renders. `refetchFailing` lets the caller mark a badge whose
 * last refetch errored as not updating too: TanStack keeps the previous data on a failed refetch, so
 * without it an unreachable API would leave a confident green pill with an ageing time beside it.
 */
export function activationViewOf(
  activation: CustomerActivation | null | undefined,
  refetchFailing = false,
): ActivationView {
  const state = activationStateOf(activation?.state);
  const tier = activationTierOf(activation?.tier);
  return {
    state,
    tier,
    display: activationDisplayOf(state, tier),
    notUpdating: activation?.stale === true || refetchFailing,
    checkedAt: activation?.checked_at ?? null,
  };
}
